import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";
import type { AccountDTO, SessionUser } from "@app/shared";
import { api } from "./api";

interface BrandContextValue {
  /** All brands the caller can see (admins: all; brand users: just their own). */
  brands: AccountDTO[];
  /** The active brand slug (admins switch it; brand users are locked to theirs). */
  activeKey: string | null;
  /** The active brand row (or null while loading / when none exist). */
  active: AccountDTO | null;
  /** Switch the active brand (admins only; no-op for brand users). */
  setActiveKey: (key: string) => void;
  /** True when the session is an admin (manages brands + configuration). */
  isAdmin: boolean;
  /** True when the session sees/operates across ALL brands (admin or manajer). */
  canSeeAllBrands: boolean;
  /** True while the brand list is loading. */
  loading: boolean;
}

const BrandContext = createContext<BrandContextValue | null>(null);

const STORAGE_KEY = "active-brand";

/**
 * Provides the multi-brand context: the brand list (from `/accounts`, already
 * scoped server-side) and the active brand. Admins pick the active brand (kept in
 * localStorage); a brand user is permanently locked to their own brand.
 */
export function BrandProvider({
  user,
  children,
}: {
  user: SessionUser;
  children: ReactNode;
}) {
  const isAdmin = user.role === "admin";
  // Admin + manajer both operate across all brands (manajer just can't manage
  // brands/config). A brand user is locked to its own brand.
  const canSeeAllBrands = isAdmin || user.role === "manajer";

  const brandsQuery = useQuery({
    queryKey: ["accounts"],
    queryFn: ({ signal }) => api.accounts(signal),
    staleTime: 60_000,
  });
  const brands = useMemo(() => brandsQuery.data ?? [], [brandsQuery.data]);

  // The all-brand viewer's last-picked brand (persisted). Brand users ignore it.
  const [stored, setStored] = useState<string | null>(() =>
    canSeeAllBrands ? localStorage.getItem(STORAGE_KEY) : null,
  );

  const activeKey = useMemo(() => {
    if (!canSeeAllBrands) return user.accountKey; // brand user: always their own
    if (stored && brands.some((b) => b.key === stored)) return stored;
    return brands[0]?.key ?? null; // default to the first brand
  }, [canSeeAllBrands, user.accountKey, stored, brands]);

  const active = useMemo(
    () => brands.find((b) => b.key === activeKey) ?? null,
    [brands, activeKey],
  );

  const setActiveKey = (key: string) => {
    if (!canSeeAllBrands) return;
    setStored(key);
    localStorage.setItem(STORAGE_KEY, key);
  };

  const value: BrandContextValue = {
    brands,
    activeKey,
    active,
    setActiveKey,
    isAdmin,
    canSeeAllBrands,
    loading: brandsQuery.isLoading,
  };

  return (
    <BrandContext.Provider value={value}>{children}</BrandContext.Provider>
  );
}

/** Access the brand context. Throws if used outside a {@link BrandProvider}. */
export function useBrands(): BrandContextValue {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error("useBrands must be used within a BrandProvider");
  return ctx;
}

/**
 * The active brand slug for the current view. May be null while loading or when
 * an admin has not created any brand yet — pages should disable brand actions
 * until it is set.
 */
export function useActiveBrand(): string | null {
  return useBrands().activeKey;
}
